import React, { useEffect, useMemo, useRef, useState } from "react";

import { useLocation } from "@gatsbyjs/reach-router";

import { css } from "@emotion/react";
import styled from "@emotion/styled";
import shallow from "zustand/shallow";

import { storageSession } from "../../../utils/StorageHandler";
import { iUrlParam, setUrlParams } from "../../../utils/urlParams";
import LoadingIndicator from "../../shared/LoadingIndicator";
import CategoryTabs from "./components/CategoryTabs";
import StorefrontUnavailable from "./components/StorefrontUnavailable";
import DataControl from "./DataControl";
import ProductList from "./ProductList";
import { lastVisitedStorePathKey } from "./ShoppingCart/store";
import useStore, { iShopwareCategory, iShopwareProduct } from "./store";

const ShopContainer = styled.div`
    position: relative;
    display: grid;
    gap: 50px;
    grid-template-columns: 1fr;
    scroll-margin: 60px;

    ${({ theme }) => theme.breakpoints.from.medium.css`
    grid-template-columns: 1fr 3fr;
  `}
`;

const ProductListContainer = styled.div`
    min-height: 25em;
`;

const loadingIndicatorStyles = css`
    padding-top: 50px;
    padding-bottom: 50px;
`;

interface iShopProps {
    location?: {
        search: string;
    };
    shopwareMainCategoryId: string;
}

const NoProducts = styled.div`
    padding-top: 40px;
    padding-bottom: 40px;
    text-align: center;
`;

const Shop = ({ shopwareMainCategoryId }: iShopProps) => {
    const location = useLocation();
    const [isShopUnavailable, setIsShopUnavailable] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const storeFrontRef = useRef();
    const {
        categories,
        fetchCategories,
        setCategories,
        selectedCategory,
        setSelectedCategory,
        fetchProductsByCategory,
        fetchProductsBySearch,
        isLoadingProducts,
        isLoadingCategories,
        sort,
        search,
        currentSort,
        searchedProducts,
        setSearch,
        _shopwareMainCategoryId,
    } = useStore(
        (state) => ({
            fetchCategories: state.fetchCategories,
            categories: state.categories,
            setCategories: state.setCategories,
            selectedCategory: state.selectedCategory,
            setSelectedCategory: state.setSelectedCategory,
            fetchProductsByCategory: state.fetchProductsByCategory,
            isLoadingProducts: state.isLoadingProducts,
            isLoadingCategories: state.isLoadingCategories,
            sort: state.sort,
            search: state.search,
            currentSort: state.currentSort,
            fetchProductsBySearch: state.fetchProductsBySearch,
            searchedProducts: state.searchedProducts,
            setSearch: state.setSearch,
            _shopwareMainCategoryId: state.shopwareMainCategoryId,
        }),
        shallow
    );

    const params = useMemo(
        () => new URLSearchParams(location?.search ?? ""),
        [location]
    );

    const fetchAvailableCategories = async () => {
        try {
            const categories_ = await fetchCategories(shopwareMainCategoryId);
            setCategories(categories_);
            if (
                params.has("category") &&
                categories_.find(
                    (category) => category.id === params.get("category")
                )
            ) {
                // We are checking in the if statement, for the category to be set.
                setSelectedCategory(params.get("category") ?? undefined);
            } else if (categories_[0]?.id) {
                setSelectedCategory(categories_[0]?.id);
            }
        } catch (error) {
            setIsShopUnavailable(true);
        }
    };

    useEffect(() => {
        storageSession.set(lastVisitedStorePathKey, location.pathname);
        void fetchAvailableCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_shopwareMainCategoryId]);

    useEffect(() => {
        const params: iUrlParam[] = [
            { name: "storefrontPath", value: location.pathname },
        ];
        if (selectedCategory) {
            params.push({ name: "category", value: selectedCategory });
            storageSession.set(
                lastVisitedStorePathKey,
                `${location.pathname}?storefrontPath=${location.pathname}&category=${selectedCategory}`
            );
        }
        setUrlParams(params);
    }, [selectedCategory]);

    const showCategory = (category: iShopwareCategory) => {
        setSelectedCategory(category.id);
        setSearch("");
        if (storeFrontRef.current && !isFirstLoad) {
            storeFrontRef.current.scrollIntoView({
                block: "start",
            });
        }
    };

    useEffect(() => {
        const fetch = async () => {
            if (selectedCategory) {
                await fetchProductsByCategory(selectedCategory);
                if (isFirstLoad) {
                    if (params.has("scroll")) {
                        const scroll = parseInt(params.get("scroll"));
                        window.scrollTo(0, scroll);
                    }
                    setIsFirstLoad(false);
                }
            }
        };
        try {
            if (shopwareMainCategoryId === _shopwareMainCategoryId) {
                void fetch();
            }
        } catch (error) {
            setIsShopUnavailable(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory]);

    useEffect(() => {
        const fetch = async () => {
            if (
                sort.key !== currentSort &&
                !isLoadingProducts &&
                !isLoadingCategories &&
                selectedCategory
            ) {
                await fetchProductsByCategory(selectedCategory);
            }
        };
        try {
            if (shopwareMainCategoryId === _shopwareMainCategoryId) {
                void fetch();
            }
        } catch (error) {
            setIsShopUnavailable(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort]);

    useEffect(() => {
        const fetch = async () => {
            if (
                !isLoadingProducts &&
                !isLoadingCategories &&
                search !== "" &&
                _shopwareMainCategoryId
            ) {
                await fetchProductsBySearch(_shopwareMainCategoryId);
            }
        };
        try {
            if (shopwareMainCategoryId === _shopwareMainCategoryId) {
                void fetch();
            }
        } catch (error) {
            setIsShopUnavailable(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, sort]);

    const filteredProducts: Array<iShopwareProduct> | null = useMemo(() => {
        if (selectedCategory) {
            const category = categories.find(
                (category) => category.id === selectedCategory
            );
            if (category && category.products) {
                return category.products;
            }
        }
        return [];
    }, [selectedCategory, categories]);

    if (isShopUnavailable) {
        return <StorefrontUnavailable />;
    }

    return (
        <div className="storefront">
            {isLoadingCategories && (
                <LoadingIndicator css={loadingIndicatorStyles} />
            )}
            {!isLoadingCategories && categories.length > 0 && (
                <ShopContainer ref={storeFrontRef}>
                    <CategoryTabs
                        categories={categories}
                        selectedCategory={selectedCategory}
                        onClick={(category) => showCategory(category)}
                    />
                    <ProductListContainer>
                        {isLoadingProducts && (
                            <LoadingIndicator css={loadingIndicatorStyles} />
                        )}
                        {!isLoadingProducts &&
                            search === "" &&
                            filteredProducts.length > 0 && (
                                <>
                                    <DataControl
                                        selectedCategory={selectedCategory}
                                        categories={categories}
                                    />
                                    <ProductList products={filteredProducts} />
                                </>
                            )}
                        {!isLoadingProducts &&
                            search !== "" &&
                            searchedProducts.length > 0 && (
                                <>
                                    <DataControl
                                        selectedCategory={selectedCategory}
                                        categories={categories}
                                    />
                                    <ProductList products={searchedProducts} />
                                </>
                            )}
                        {!isLoadingProducts &&
                            search === "" &&
                            filteredProducts.length === 0 && (
                                <NoProducts>
                                    Für diese Kategorie gibt es zurzeit keine
                                    Artikel
                                </NoProducts>
                            )}
                        {!isLoadingProducts &&
                            search !== "" &&
                            searchedProducts.length === 0 && (
                                <NoProducts>
                                    Es konnten leider keine Ergebnisse zu dem
                                    Suchbegriff &quot;{search}&quot; gefunden
                                    werden.
                                </NoProducts>
                            )}
                    </ProductListContainer>
                </ShopContainer>
            )}
        </div>
    );
};

export default Shop;
